import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { CaretRight, CaretDown, Trash, Buildings } from '@phosphor-icons/react';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;

function flatten(nodes, collapsed) {
  const byParent = {};
  nodes.forEach((n) => { const k = n.parent_id || 'ROOT'; (byParent[k] = byParent[k] || []).push(n); });
  const out = [];
  const stack = (byParent.ROOT || []).map((n) => ({ node: n, depth: 0 })).reverse();
  while (stack.length) {
    const { node, depth } = stack.pop();
    const kids = byParent[node.id] || [];
    out.push({ node, depth, hasKids: kids.length > 0 });
    if (!collapsed.has(node.id)) {
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], depth: depth + 1 });
    }
  }
  return out;
}

export const HierarchyTab = () => {
  const qc = useQueryClient();
  const { data: model } = useQuery({ queryKey: ['acad-model'], queryFn: () => api.get('/acad/model').then((r) => r.data) });
  const { data: nodes } = useQuery({ queryKey: ['acad-nodes'], queryFn: () => api.get('/acad/nodes').then((r) => r.data) });
  const { data: district } = useQuery({ queryKey: ['acad-district'], retry: false, queryFn: () => api.get('/acad/district').then((r) => r.data).catch(() => null) });
  const [dragId, setDragId] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [addingTo, setAddingTo] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [txt, setTxt] = useState('');
  const [rootTxt, setRootTxt] = useState('');
  const levels = model?.levels || [];
  const rows = flatten(nodes || [], collapsed);
  const refresh = () => qc.invalidateQueries({ queryKey: ['acad-nodes'] });
  const toggleOpen = (id) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id); else next.add(id);
    setCollapsed(next);
  };
  const onAdd = async (name, parent_id, level_label) => {
    try { await api.post('/acad/nodes', { name, parent_id, level_label }); toast.success(`${level_label} added`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onMove = async (id, parent_id) => {
    try { await api.patch(`/acad/nodes/${id}`, { parent_id }); toast.success('Unit moved'); refresh(); } catch (e) { toast.error(errMsg(e)); }
    setDragId(null);
  };
  const onRename = async (id, name) => {
    if (!name.trim()) return;
    try { await api.patch(`/acad/nodes/${id}`, { name }); toast.success('Renamed'); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (id) => {
    try { await api.delete(`/acad/nodes/${id}`); toast.success('Unit removed'); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {district?.multi && (
        <Card testid="district-overview">
          <p className="font-display font-bold mb-2 flex items-center gap-1.5"><Buildings size={18} className="text-orange-600" /> Institutions in your district</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {district.institutions.map((t) => (
              <div key={t.id} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid={`district-inst-${t.id}`}>
                <b>{t.name}</b> <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t.type}</span>
                <p className="text-xs text-slate-500 mt-1">{t.students} students · {t.terms} terms · {t.sections} sections</p>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card testid="hierarchy-tree">
        <p className="font-display font-bold mb-1">Institution structure</p>
        {!levels.length && <p className="text-sm text-slate-400">Choose an academic model on the Overview tab first.</p>}
        {levels.length > 0 && (
          <>
            <p className="text-xs text-slate-500 mb-3">Levels: {levels.join(' > ')}. Drag a unit onto another to move it.</p>
            <div data-testid="hier-root-zone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (dragId) onMove(dragId, 'ROOT'); }}
              className="border border-dashed border-slate-200 rounded-lg p-2 min-h-[60px]">
              {rows.map(({ node, depth, hasKids }) => {
                const myIx = levels.indexOf(node.level_label);
                const childLevel = myIx >= 0 && myIx + 1 < levels.length ? levels[myIx + 1] : null;
                return (
                  <div key={node.id} style={{ marginInlineStart: depth * 22 }}>
                    <div draggable data-testid={`hier-node-${node.id}`}
                      onDragStart={(e) => { e.stopPropagation(); setDragId(node.id); }}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (dragId && dragId !== node.id) onMove(dragId, node.id); }}
                      className="flex items-center gap-1.5 text-sm py-1 px-1 rounded hover:bg-orange-50/60 cursor-grab group border-s border-slate-100 ps-2">
                      <button onClick={() => toggleOpen(node.id)} className="text-slate-400 w-3.5">
                        {hasKids ? (collapsed.has(node.id) ? <CaretRight size={12} /> : <CaretDown size={12} />) : null}
                      </button>
                      {renamingId === node.id ? (
                        <Input autoFocus className="h-7 w-44" defaultValue={node.name} data-testid={`hier-rename-input-${node.id}`}
                          onKeyDown={(e) => { if (e.key === 'Enter') { onRename(node.id, e.target.value); setRenamingId(null); } if (e.key === 'Escape') setRenamingId(null); }} />
                      ) : (
                        <span><b>{node.name}</b>{node.code ? <span className="text-xs text-slate-400"> {node.code}</span> : null}</span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{node.level_label}</span>
                      <span className="opacity-0 group-hover:opacity-100 flex gap-1.5 transition-opacity">
                        {childLevel && <button data-testid={`hier-add-${node.id}`} onClick={() => { setAddingTo(addingTo === node.id ? null : node.id); setTxt(''); }} className="text-xs text-orange-600 underline">+ {childLevel}</button>}
                        <button data-testid={`hier-rename-${node.id}`} onClick={() => setRenamingId(node.id)} className="text-xs text-slate-500 underline">Rename</button>
                        <button data-testid={`hier-del-${node.id}`} onClick={() => onDelete(node.id)} className="text-rose-400"><Trash size={13} /></button>
                      </span>
                    </div>
                    {addingTo === node.id && childLevel && (
                      <div className="ms-8 my-1">
                        <Input autoFocus className="h-7 w-56" placeholder={`New ${childLevel} name (Enter to save)`} value={txt} data-testid={`hier-add-input-${node.id}`}
                          onChange={(e) => setTxt(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && txt.trim()) { onAdd(txt.trim(), node.id, childLevel); setTxt(''); setAddingTo(null); } if (e.key === 'Escape') setAddingTo(null); }} />
                      </div>
                    )}
                  </div>
                );
              })}
              {rows.length === 0 && <p className="text-sm text-slate-400 p-2">No units yet - add your first {levels[0]} below.</p>}
            </div>
            <div className="flex gap-2 mt-3">
              <Input data-testid="hier-root-input" className="w-64" placeholder={`New ${levels[0]} name`} value={rootTxt} onChange={(e) => setRootTxt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && rootTxt.trim()) { onAdd(rootTxt.trim(), null, levels[0]); setRootTxt(''); } }} />
              <button data-testid="hier-root-add" disabled={!rootTxt.trim()} onClick={() => { onAdd(rootTxt.trim(), null, levels[0]); setRootTxt(''); }}
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Add {levels[0]}</button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
